import {
  Box,
  TextField,
  MenuItem,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAssessmentReports,
  fetchAssessmentDrilldown,
  fetchAssessors,
  fetchAssessees,
  fetchAssesseeQuestionReport,
} from "reduxStore/actions/assessmentActions";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import NoAssessments from "./noAssessments";
import dayjs from "dayjs";
import "dayjs/locale/en-gb";
import { DataGrid } from "@mui/x-data-grid";
import { checkRoles, getPermissionFlags, getUserScope } from "utils/permissions";
import { fetchAllLocations } from "reduxStore/actions/locationActions";
import { fetchDivisionForOrganisation } from "reduxStore/actions/divisionActions";
import { fetchLevelsForOrganisation } from "reduxStore/actions/levelActions";
import { fetchPrinciples } from "reduxStore/actions/principleActions";

function AssessmentReports({
  isPrinciplesGraph,
  isCoachingFeed = false,
  viewingUserId = "",
  filtersOpen,
  setFiltersOpen,
  isXLFilters,
}) {
  const dispatch = useDispatch();
  const isSmallScreen = useMediaQuery("(max-width:900px)");
  const isXLMedia = useMediaQuery("(min-width:1800px)");
  const effectiveXL =
    typeof isXLFilters === "boolean" ? isXLFilters : isXLMedia;
  const leftMargin = isSmallScreen ? 80 : 320;

  const { palette } = useTheme();
  const primaryMediumMain = palette.primary.mediumMain;
  const secondaryMain = palette.secondary.mediumMain;
  const neutralDark = palette.neutral.dark;
  const neutralLight = palette.neutral.light;

  const assessmentReports =
    useSelector((state) => state.assessmentReports) || [];

  const user = useSelector((state) => state.user);
  const { organisationId, locationIds, divisionIds, levelIds } = getUserScope(user);
  const userOrg = organisationId || "";
  const userLocation = locationIds[0] || "";
  const userDivision = divisionIds[0] || "";
  const userLevel = levelIds[0] || "";
  const { showOrg, showLocation, showDivision, showLevel, showSeniorLeader } =
    getPermissionFlags(user);
  const { isAcademyImprovementLead } = checkRoles(user);

  const [selectedLocation, setSelectedLocation] = useState("");
  const [selectedDivision, setSelectedDivision] = useState("");
  const [selectedLevel, setSelectedLevel] = useState("");
  const [selectedAssessor, setSelectedAssessor] = useState("");
  const [selectedAssessee, setSelectedAssessee] = useState("");
  const [startDate, setStartDate] = useState(dayjs().subtract(12, "month"));
  const [endDate, setEndDate] = useState(dayjs());
  const [selectedPrinciple, setSelectedPrinciple] = useState("");
  const [drilldown, setDrilldown] = useState({
    open: false,
    title: "",
    loading: false,
    entries: [],
  });
  const [drilldownPageSize, setDrilldownPageSize] = useState(10);

  const locations = useSelector((state) => state.locationsAll) || [];
  const divisions = useSelector((state) => state.divisionsByLocation) || [];
  const levels = useSelector((state) => state.levelsForOrganisation) || [];
  const assessors = useSelector((state) => state.assessors) || [];
  const assesseeNames = useSelector((state) => state.assessees) || [];
  const principleNames = useSelector((state) => state.principles) || [];

  const chartDataByQuestion = assessmentReports.byQuestion || [];

  const chartDataByPrinciple = assessmentReports.byPrinciple || [];

  const rowHeight = 25; // Adjust as needed
  const chartHeightByQuestion = Math.max(
    400,
    chartDataByQuestion.length * rowHeight
  );
  const chartHeightByPrinciple = Math.max(
    400,
    chartDataByPrinciple.length * rowHeight
  );

  const [internalFiltersOpen, setInternalFiltersOpen] = useState(effectiveXL);
  const filtersOpenValue =
    typeof filtersOpen === "boolean" ? filtersOpen : internalFiltersOpen;
  const setFiltersOpenValue =
    typeof setFiltersOpen === "function"
      ? setFiltersOpen
      : setInternalFiltersOpen;

  useEffect(() => {
    setFiltersOpenValue(effectiveXL);
  }, [effectiveXL, setFiltersOpenValue]);

  // Create a set of unique questions for the Y-axis
  const CustomYAxisTick = ({ x, y, payload }) => {
    const text = payload.value;
    const dataRow = chartDataByQuestion.find((r) => r.question === text);

    const canSelectLocation = showOrg || isAcademyImprovementLead;
    const isHighlight =
      dataRow?.priority && (!canSelectLocation || selectedLocation !== "");

    const fontSize = 12;
    const padY = 5;
    const rectHeight = fontSize + padY * 2;

    const rectY = y - rectHeight / 2;

    return (
      <g>
        {isHighlight && (
          <rect
            x={0} // start at the far left of the chart
            y={rectY}
            width={x} // span to the x-axis tick mark
            height={rectHeight}
            fill={secondaryMain}
          />
        )}
        <text
          x={x - 14}
          y={y}
          textAnchor="end"
          alignmentBaseline="middle"
          fill={isHighlight ? neutralLight : neutralDark}
          style={{
            fontSize,
            fontWeight: "bold",
            cursor: dataRow?.url ? "pointer" : "default",
            textDecoration: dataRow?.url ? "underline" : "none",
          }}
          onClick={
            dataRow?.url ? () => window.open(dataRow.url, "_blank") : undefined
          }
        >
          {text}
        </text>
      </g>
    );
  };

  // Custom tick component for clickable y-axis labels in the principle chart
  const CustomPrincipleYAxisTick = ({ x, y, payload }) => {
    return (
      <text
        x={x - 10}
        y={y + 5}
        textAnchor="end"
        fill={neutralDark}
        style={{
          fontSize: 12,
          fontWeight: "bold",
        }}
      >
        {payload.value}
      </text>
    );
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Box
          sx={{
            backgroundColor: "white",
            border: "1px solid neutralDark",
            padding: "10px",
            borderRadius: "5px",
            color: "black",
          }}
        >
          {label}
          <br />
          <span style={{ color: primaryMediumMain }}>
            Secure: {data.strengths}% ({data.strengthsNumber})
          </span>
          <br />
          <span style={{ color: secondaryMain }}>
            Developing: {data.growthAreas}% ({data.growthAreasNumber})
          </span>
        </Box>
      );
    }

    return null;
  };

  const drilldownColumns = useMemo(
    () => [
      {
        field: "date",
        headerName: "Date",
        minWidth: 120,
        valueFormatter: ({ value }) =>
          value ? dayjs(value).format("DD/MM/YYYY") : "N/A",
      },
      {
        field: "assesseeName",
        headerName: "Assessee",
        flex: 1,
        minWidth: 180,
      },
      {
        field: "assessorName",
        headerName: "Observer",
        flex: 1,
        minWidth: 170,
      },
      {
        field: "lessonPeriod",
        headerName: "Lesson/Period",
        flex: 0.8,
        minWidth: 140,
      },
      {
        field: "rating",
        headerName: "Rating",
        minWidth: 130,
        renderCell: ({ value }) => (
          <Typography
            variant="body2"
            fontWeight="bold"
            color={value === "Secure" ? primaryMediumMain : secondaryMain}
          >
            {value || "N/A"}
          </Typography>
        ),
      },
      {
        field: "principle",
        headerName: "Principle",
        flex: 1.1,
        minWidth: 200,
      },
      {
        field: "question",
        headerName: "Question",
        flex: 1.3,
        minWidth: 260,
      },
      {
        field: "notes",
        headerName: "Notes",
        flex: 1.4,
        minWidth: 240,
      },
    ],
    [primaryMediumMain, secondaryMain]
  );

  const buildFilterPayload = () => {
    const dateFilters = {
      startDate: startDate ? startDate.toISOString() : undefined,
      endDate: endDate ? endDate.toISOString() : undefined,
    };

    if (isCoachingFeed) {
      return {
        assessee: viewingUserId,
        ...dateFilters,
      };
    }

    return {
      organisation: userOrg,
      location:
        showOrg || isAcademyImprovementLead ? selectedLocation : userLocation,
      division:
        showLevel || showSeniorLeader || isAcademyImprovementLead
          ? selectedDivision
          : userDivision,
      level:
        showDivision || showSeniorLeader || isAcademyImprovementLead
          ? selectedLevel
          : userLevel,
      assessor: selectedAssessor,
      assessee: selectedAssessee,
      ...dateFilters,
    };
  };

  const handleBarClick = async (data, ratingKey, type) => {
    if (!data) return;

    const rawQuestionText =
      type === "question"
        ? (data.fullQuestion || data.question || "")
            .replace(/^P\d+\s*-\s*/, "")
            .trim()
        : undefined;
    const useQuestionFallback = type === "question" && !data.questionId;
    const ratingLabel = ratingKey === "strengths" ? "Secure" : "Developing";
    const titlePrefix =
      type === "question"
        ? data.fullQuestion || data.question
        : data.fullPrinciple || data.principle;

    setDrilldown((prev) => ({
      ...prev,
      open: true,
      loading: true,
      title: `${titlePrefix} — ${ratingLabel}`,
      entries: [],
    }));

    const response = await dispatch(
      fetchAssessmentDrilldown({
        ...buildFilterPayload(),
        questionId: type === "question" ? data.questionId : undefined,
        principleId: data.principleId,
        rating: ratingKey,
        questionText:
          useQuestionFallback && rawQuestionText ? rawQuestionText : undefined,
        principleNumber: useQuestionFallback ? data.principleNumber : undefined,
      })
    );

    setDrilldown((prev) => ({
      ...prev,
      loading: false,
      entries: (response?.entries || []).map((entry, index) => ({
        ...entry,
        assessmentId: entry.id,
        id: entry.id ? `${entry.id}-${index}` : `row-${index}`,
      })),
    }));
  };

  const handleCloseDrilldown = () =>
    setDrilldown((prev) => ({ ...prev, open: false }));

  useEffect(() => {
    if (isCoachingFeed && viewingUserId) {
      dispatch(
        fetchAssesseeQuestionReport({
          assessee: viewingUserId,
          startDate: startDate ? startDate.toISOString() : undefined,
          endDate: endDate ? endDate.toISOString() : undefined,
        })
      );

      return;
    }

    if (userOrg) {
      dispatch(
        fetchAssessmentReports({
          organisation: userOrg,
          location:
            showOrg || isAcademyImprovementLead
              ? selectedLocation
              : userLocation,
          division:
            showLevel || showSeniorLeader || isAcademyImprovementLead
              ? selectedDivision
              : userDivision,
          level:
            showDivision || showSeniorLeader || isAcademyImprovementLead
              ? selectedLevel
              : userLevel,
          assessor: selectedAssessor,
          assessee: selectedAssessee,
          startDate: startDate ? startDate.toISOString() : undefined,
          endDate: endDate ? endDate.toISOString() : undefined,
          selectedPrinciple,
        })
      );
    }
  }, [
    dispatch,
    userOrg,
    selectedLocation,
    selectedDivision,
    selectedAssessor,
    selectedAssessee,
    selectedLevel,
    startDate,
    endDate,
    selectedPrinciple,
    showOrg,
    showSeniorLeader,
    showLocation,
    showLevel,
    showDivision,
    userLocation,
    userDivision,
    userLevel,
    isAcademyImprovementLead,
    isCoachingFeed,
    viewingUserId,
  ]);

  useEffect(() => {
    dispatch(fetchAllLocations());
    dispatch(
      fetchDivisionForOrganisation({
        organisationId: userOrg,
        locationId:
          showOrg || isAcademyImprovementLead ? selectedLocation : userLocation,
      })
    );
    dispatch(
      fetchLevelsForOrganisation({
        organisationId: userOrg,
        locationId:
          showOrg || isAcademyImprovementLead ? selectedLocation : userLocation,
      })
    );
    dispatch(
      fetchAssessors({
        organisation: userOrg,
        location:
          showOrg || isAcademyImprovementLead ? selectedLocation : userLocation,
      })
    );
    dispatch(
      fetchAssessees({
        organisation: userOrg,
        location:
          showOrg || isAcademyImprovementLead ? selectedLocation : userLocation,
      })
    );
  }, [
    dispatch,
    showOrg,
    selectedLocation,
    userLocation,
    userOrg,
    isAcademyImprovementLead,
  ]);

  useEffect(() => {
    dispatch(fetchPrinciples());
  }, [dispatch]);

  if (!assessmentReports) {
    return <NoAssessments />;
  }

  return (
    <Box
      width="100%"
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "2rem",
      }}
      my={2}
    >
      <Collapse in={filtersOpenValue || effectiveXL} timeout="auto">
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
          {(showOrg || isAcademyImprovementLead) && !isCoachingFeed && (
            <TextField
              select
              label="School"
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
              sx={{ minWidth: 200 }}
              size="small"
            >
              <MenuItem value="">All Locations</MenuItem>
              {locations.map((loc) => (
                <MenuItem key={loc._id} value={loc._id}>
                  {loc.name}
                </MenuItem>
              ))}
            </TextField>
          )}

          {(showLevel || showSeniorLeader || isAcademyImprovementLead) &&
            !isCoachingFeed && (
              <TextField
                select
                label="Department"
                value={selectedDivision}
                onChange={(e) => setSelectedDivision(e.target.value)}
                sx={{ minWidth: 200 }}
                size="small"
              >
                <MenuItem value="">All Divisions</MenuItem>
                {divisions.map((div) => (
                  <MenuItem key={div._id} value={div._id}>
                    {div.name}
                  </MenuItem>
                ))}
              </TextField>
            )}

          {(showDivision || showSeniorLeader || isAcademyImprovementLead) &&
            !isCoachingFeed && (
              <TextField
                select
                label="Year"
                value={selectedLevel}
                onChange={(e) => setSelectedLevel(e.target.value)}
                sx={{ minWidth: 200 }}
                size="small"
              >
                <MenuItem value="">All Levels</MenuItem>
                {levels.map((lvl) => (
                  <MenuItem key={lvl._id} value={lvl._id}>
                    {lvl.name}
                  </MenuItem>
                ))}
              </TextField>
            )}

          {!isCoachingFeed && (
            <TextField
              select
              label="Assessor"
              value={selectedAssessor}
              onChange={(e) => setSelectedAssessor(e.target.value)}
              sx={{ minWidth: 200 }}
              size="small"
            >
              <MenuItem value="">All Assessors</MenuItem>
              {assessors.map((assessor) => (
                <MenuItem key={assessor._id} value={assessor._id}>
                  {assessor.firstName} {assessor.lastName}
                </MenuItem>
              ))}
            </TextField>
          )}

          {(showSeniorLeader || isAcademyImprovementLead) &&
            !isCoachingFeed && (
              <TextField
                select
                label="Assessee"
                value={selectedAssessee}
                onChange={(e) => setSelectedAssessee(e.target.value)}
                sx={{ minWidth: 200 }}
                size="small"
              >
                <MenuItem value="">All Assessments</MenuItem>
                {assesseeNames.map((name, index) => (
                  <MenuItem key={index} value={name}>
                    {name}
                  </MenuItem>
                ))}
              </TextField>
            )}

          {!isPrinciplesGraph && !isCoachingFeed && (
            <TextField
              select
              label="Principle"
              value={selectedPrinciple}
              onChange={(e) => setSelectedPrinciple(e.target.value)}
              sx={{ minWidth: 200 }}
              size="small"
            >
              <MenuItem value="">All Principles</MenuItem>
              {principleNames.map((principle) => (
                <MenuItem key={principle._id} value={principle._id}>
                  {principle.principleNumber}: {principle.name}
                </MenuItem>
              ))}
            </TextField>
          )}

          <Box
            display="flex"
            gap={2}
            alignItems="center"
            sx={{ minWidth: 200 }}
          >
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                format="DD/MM/YYYY"
                slotProps={{
                  textField: { size: "small", sx: { minWidth: 200 } },
                }}
              />
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                format="DD/MM/YYYY"
                slotProps={{
                  textField: { size: "small", sx: { minWidth: 200 } },
                }}
              />
            </LocalizationProvider>
          </Box>
        </Box>
      </Collapse>

      {/* Charts Section */}

      {!isPrinciplesGraph && (
        <ResponsiveContainer width="100%" height={chartHeightByQuestion}>
          {/* Question Chart with clickable y-axis labels */}
          <BarChart
            width={1300}
            height={chartHeightByQuestion}
            data={chartDataByQuestion}
            layout="vertical"
            margin={{ top: 20, right: 50, left: leftMargin, bottom: 50 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              type="number"
              domain={[0, 100]}
              label={{
                value: "Percentage",
                position: "insideBottom",
                offset: -5,
              }}
              tick={{ fontSize: 12, fontWeight: "bold" }}
            />
            <YAxis
              type="category"
              dataKey="question"
              tick={<CustomYAxisTick />}
              tickMargin={16}
              interval={0}
            />
            <Tooltip content={<CustomTooltip />} />

            <Legend
              verticalAlign="top"
              align="center"
              wrapperStyle={{
                top: 10,
                left: 400,
                lineHeight: "5px",
              }}
            />
            <Bar
              dataKey="strengths"
              stackId="total"
              fill={primaryMediumMain}
              name="Secure"
              onClick={(data) => handleBarClick(data, "strengths", "question")}
            />
            <Bar
              dataKey="growthAreas"
              stackId="total"
              fill={secondaryMain}
              name="Developing"
              onClick={(data) =>
                handleBarClick(data, "growthAreas", "question")
              }
            />
          </BarChart>
        </ResponsiveContainer>
      )}

      {isPrinciplesGraph && (
        <ResponsiveContainer width="100%" height={chartHeightByPrinciple}>
          <BarChart
            width={1300}
            height={chartHeightByPrinciple}
            data={chartDataByPrinciple}
            layout="vertical"
            margin={{ top: 20, right: 50, left: leftMargin, bottom: 50 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              type="number"
              domain={[0, 100]}
              label={{
                value: "Percentage",
                position: "insideBottom",
                offset: -5,
              }}
              tick={{ fontSize: 12, fontWeight: "bold" }}
            />
            <YAxis
              type="category"
              dataKey="principle"
              tick={<CustomPrincipleYAxisTick />}
              interval={0}
            />
            <Tooltip content={<CustomTooltip />} />

            <Legend
              verticalAlign="top"
              align="center"
              wrapperStyle={{
                top: 10,
                left: 400,
                lineHeight: "5px",
              }}
            />
            <Bar
              dataKey="strengths"
              stackId="total"
              fill={primaryMediumMain}
              name="Secure"
              onClick={(data) => handleBarClick(data, "strengths", "principle")}
            />
            <Bar
              dataKey="growthAreas"
              stackId="total"
              fill={secondaryMain}
              name="Developing"
              onClick={(data) =>
                handleBarClick(data, "growthAreas", "principle")
              }
            />
          </BarChart>
        </ResponsiveContainer>
      )}

      <Dialog
        open={drilldown.open}
        onClose={handleCloseDrilldown}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>{drilldown.title || "Lesson Visits"}</DialogTitle>
        <DialogContent dividers>
          {drilldown.loading ? (
            <Typography>Loading...</Typography>
          ) : drilldown.entries.length === 0 ? (
            <Typography>No lesson visits found for this segment.</Typography>
          ) : (
            <Box
              sx={{
                maxHeight: 500,
                overflow: "auto",
                "& .MuiDataGrid-cell": {
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  lineHeight: 1.4,
                  alignItems: "flex-start",
                },
                "& .MuiDataGrid-row": {
                  maxHeight: "none",
                  minHeight: "52px",
                },
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: neutralLight,
                },
              }}
            >
              <DataGrid
                rows={drilldown.entries}
                columns={drilldownColumns}
                getRowId={(row) => row.id || row._id}
                autoHeight
                disableSelectionOnClick
                density="compact"
                pageSize={drilldownPageSize}
                onPageSizeChange={(newPageSize) =>
                  setDrilldownPageSize(newPageSize)
                }
                rowsPerPageOptions={[5, 10, 25, 50]}
              />
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default AssessmentReports;
